import React from "react";
import { Sparkle, ArrowUpRight } from "lucide-react";

const cases = [
  {
    category: "Strategic Partnerships",
    title: "A heritage maison enters the next generation",
    body: "Architected a cross-category partnership program that introduced a 60-year-old fashion house to a new Gen-Z editorial audience without diluting equity.",
    metric: "+312%",
    metricLabel: "Earned reach in 90 days",
    image:
      "https://images.unsplash.com/photo-1543728069-a3f97c5a2f32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBmYXNoaW9uJTIwZXZlbnQlMjBydW53YXklMjBkYXJrfGVufDB8fHx8MTc3ODc4NjU2MHww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-7",
    aspect: "aspect-[4/3]",
  },
  {
    category: "NYC Activation",
    title: "A clean beauty launch that became a press dinner",
    body: "Produced an intimate 24-seat dinner in SoHo for top tier editors and tastemaker creators — generating 11 print stories and a sell-through in 9 days.",
    metric: "9 DAYS",
    metricLabel: "to first sell-out",
    image:
      "https://images.unsplash.com/photo-1733322992706-1210ca79f4df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBmYXNoaW9uJTIwZXZlbnQlMjBydW53YXklMjBkYXJrfGVufDB8fHx8MTc3ODc4NjU2MHww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-5",
    aspect: "aspect-[4/5]",
  },
  {
    category: "Influencer Marketing",
    title: "Launching a hospitality brand into the cultural conversation",
    body: "Curated a 14-creator residency program across NYC, Miami and CDMX — translating storytelling into reservations and 4.2x ROAS within the first quarter.",
    metric: "4.2x",
    metricLabel: "Return on ad spend",
    image:
      "https://images.unsplash.com/photo-1460551882935-745bdcaf8009?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxuZXclMjB5b3JrJTIwYXJjaGl0ZWN0dXJlJTIwYWJzdHJhY3QlMjBibGFjayUyMGFuZCUyMHdoaXRlfGVufDB8fHx8MTc3ODc4NjU2MHww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-12",
    aspect: "aspect-[16/9]",
  },
];

export default function CaseStudies() {
  return (
    <section
      id="work"
      data-testid="cases-section"
      className="relative bg-[#050505] py-16 md:py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-3">
            <span className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5]">
              ✦ Selected work
            </span>
            <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-white/40">
              Page 06
            </p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight font-light">
              Quiet briefs.
              <span className="italic text-[#7FB3D5]"> Loud results.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {cases.map((c, i) => (
            <article
              key={i}
              data-testid={`case-card-${i}`}
              className={`col-span-12 ${c.span} group relative bg-[#0a0a0a] border border-white/5 hover:border-[#7FB3D5]/40 transition-all duration-500 overflow-hidden`}
            >
              <div className={`relative ${c.aspect} overflow-hidden`}>
                <img
                  src={c.image}
                  alt={c.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1500ms] group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <Sparkle className="w-3 h-3 text-[#7FB3D5]" fill="#7FB3D5" />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white">
                    {c.category}
                  </span>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-display text-xl md:text-3xl text-white font-light leading-tight max-w-2xl">
                    {c.title}
                  </h3>
                  <div className="hidden sm:block text-right shrink-0">
                    <div className="font-display text-3xl md:text-5xl text-[#7FB3D5] font-light">
                      {c.metric}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.3em] text-white/50 mt-1">
                      {c.metricLabel}
                    </div>
                  </div>
                </div>
                <p className="mt-5 text-sm md:text-base text-white/60 leading-relaxed max-w-2xl">
                  {c.body}
                </p>
                <div className="mt-6 flex items-center justify-between">
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/70 hover:text-[#7FB3D5] transition-colors duration-300"
                  >
                    Request the case study
                    <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.2} />
                  </a>
                  <div className="sm:hidden text-right shrink-0">
                    <div className="font-display text-2xl text-[#7FB3D5] font-light">
                      {c.metric}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.3em] text-white/50 mt-1">
                      {c.metricLabel}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
